import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import DialogModal from "./dailyProgressModal";
import SidebarItem from "./sidebarItem";
import useAuth from "../../hooks/useAuth";
import toast from "react-hot-toast";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import InstantChatModal from "./instantChatModal";
import SubscriptionModal from "./subscriptionModal";
import Person from "@mui/icons-material/AccountCircle";
import ChatHistoryModal from "./ChatHistoryModal";
import axios from "axios";
import PreviewModal from "./PreviewModal";
import AscendraPlusModal from "./AscendraPlusModal";
import PaymentModal from "./paymentModal";
import AssignmentsModal from "./AssignmentModal";
import ProfileImageModal from "./ProfileImageModal";
import SupportModal from "./SupportModal";
import { useTheme } from "../../context/ThemeContext";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage, auth } from "../../firebase.config";
import {
  processImageToCircularPNG,
  validateImageFile,
} from "../../utils/imageUtils";
import { useNavigate } from "react-router-dom";

const Sidebar = ({
  progressData,
  isSidebarOpen,
  setIsSidebarOpen,
  toggleSidebar,
  courseDay,
}) => {
  const [selectedDayProgress, setSelectedDayProgress] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showInstantChat, setShowInstantChat] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showAssignments, setShowAssignments] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedAssignmentDay, setSelectedAssignmentDay] = useState(null);
  const [assignments, setAssignments] = useState({});
  const { user, logout, loading } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [openItemLabel, setOpenItemLabel] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showChatHistoryModal, setShowChatHistoryModal] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [history, setShowChatHistory] = useState([]);
  const [dayItems, setDayItems] = useState([]);
  const [course_day, setCourseDay] = useState([]);
  const [diffDays, setDiffDays] = useState(null);
  const [isSelectedPlan, setIsSelectedPlan] = useState(false);
  const [profileImage, setProfileImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSupport, setShowSupport] = useState(false);

  const groupedCourseData = {};
  const chatHistoryGroupedCourseData = {};
  const uniqueDayList = new Set();

  const handleItemClick = (label) => {
    setOpenItemLabel((prev) => {
      if (label === "victories") return "victories";
      if (label === "assignments")
        return prev === "assignments" ? null : "assignments";
      return prev === label ? null : label;
    });
  };

  const handleInstinctFormulaPay = () => {
    if (diffDays) {
      setShowPayment(true);
    }
  };

  const handleAssignmentClick = (dayNumber, assignment) => {
    setSelectedAssignmentDay(dayNumber);
    setSelectedAssignment(assignment);
    setShowAssignments(true);
  };
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    const { valid, error } = validateImageFile(file);

    if (!valid) {
      if (error) toast.error(error);
      return;
    }

    setUploadingImage(true);

    try {
      const currentUser = auth.currentUser || user;
      if (!currentUser) throw new Error("Please log in to upload image");

      toast.loading("Processing image...", { id: "processing" });
      const processedBlob = await processImageToCircularPNG(file);
      toast.dismiss("processing");

      const processedFile = new File(
        [processedBlob],
        `profile_${Date.now()}.png`,
        { type: "image/png" }
      );

      toast.loading("Uploading image...", { id: "uploading" });
      const storageRef = ref(
        storage,
        `profile-images/${currentUser.uid}/${processedFile.name}`
      );
      await uploadBytes(storageRef, processedFile);
      const downloadURL = await getDownloadURL(storageRef);
      toast.dismiss("uploading");

      await updateDoc(doc(db, "users", currentUser.uid), {
        profileImage: downloadURL,
        updatedAt: new Date(),
      });

      setProfileImage(downloadURL);
      toast.success("Profile image updated successfully!");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error(error.message || "Failed to upload image. Please try again.");
    } finally {
      setUploadingImage(false);
      toast.dismiss("processing");
      toast.dismiss("uploading");
    }
  };

  progressData.forEach((entry) => {
    const day = entry.courseDay;

    if (
      !Array.isArray(entry.courseData) ||
      entry.courseData.length === 0 ||
      entry.courseData.every((obj) => Object.keys(obj).length === 0)
    ) {
      return;
    }

    if (!chatHistoryGroupedCourseData[day]) {
      chatHistoryGroupedCourseData[day] = [];
    }

    chatHistoryGroupedCourseData[day].push(...entry.courseData);
    const filteredCourseData = entry.courseData.filter(
      (obj) => obj.victory_text !== "null"
    );

    if (filteredCourseData.length === 0) {
      return;
    }

    if (!groupedCourseData[day]) {
      groupedCourseData[day] = [];
    }
    if (entry.createdAt) {
      uniqueDayList.add(entry.createdAt);
    }
    groupedCourseData[day].push(...entry.courseData);
  });
  const uniqueDayTImestampList = Array.from(uniqueDayList);

  const fetchChatHistory = (selectedDay) => {
    setSelectedDay(selectedDay);

    if (user) {
      axios
        .get(
          `https://us-central1-rise-lean-uat.cloudfunctions.net/getChatHistory?user_id=${user?.uid}&course_day=${selectedDay}'`
        )
        .then((response) => {
          setShowChatHistory(response?.data?.chatHistory);
        })
        .catch((error) => {
          console.error("Error fetching links:", error);
        });
    }

    setShowChatHistoryModal(true);
  };

  window.addEventListener("courseDayUpdated", () => {
    const newDay = localStorage.getItem("courseDay");
    setCourseDay(newDay);
  });

  useEffect(() => {
    const fromDayNo = 0;
    if (course_day) {
      axios
        .get(
          `https://us-central1-rise-lean-uat.cloudfunctions.net/fetchBucketItems?fromDayNumber=${fromDayNo}&toDayNumber=${course_day}`
        )
        .then((response) => {
          setDayItems(response?.data?.data?.days);
        })
        .catch((error) => {
          console.error("Error fetching links:", error);
        });
    }
  }, [course_day]);

  // Fetch assignments and profile image from Firestore in real-time
  useEffect(() => {
    if (!user?.uid) return;

    const unsubscribe = onSnapshot(
      doc(db, "users", user.uid),
      (userDoc) => {
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userAssignments = userData?.assignments || {};
          setAssignments(userAssignments);
          setProfileImage(userData?.profileImage || null);
          setHasActiveSubscription(userData?.subscription_status || false);
          console.log("Assignments loaded:", userAssignments);
        }
      },
      (error) => {
        console.error("Error fetching assignments:", error);
      }
    );

    return () => unsubscribe();
  }, [user?.uid]);

  useEffect(() => {
    const diffDays = localStorage.getItem("diffDays");
    const isSelectedPlan = localStorage.getItem("isSelectedPlan") === "true";
    if (diffDays) {
      setDiffDays(diffDays);
      setIsSelectedPlan(isSelectedPlan);
    }
  }, [course_day]);

  const handleShowVictories = () => {
    handleItemClick("victories");
    setSelectedDayProgress(groupedCourseData);
    setShowModal(true);
  };

  const renderCalendar = () => {
    return (
      <div className={`p-2 ${theme === "dark" ? "bg-slate-800" : "bg-white"}`}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DateCalendar
            value={selectedDate}
            onChange={(newDate) => {
              setSelectedDate(newDate);
              fetchChatHistory(newDate);
            }}
            shouldDisableDate={(date) => {
              const formatted = date.toDateString();
              return !uniqueDayTImestampList.some(
                (ts) => ts.toDate().toDateString() === formatted
              );
            }}
            sx={{
              width: "100%",
              height: "300px",
              color: theme === "dark" ? "white" : "black",
              "& .MuiPickersDay-root": {
                fontSize: "0.75rem",
                color: theme === "dark" ? "white" : "black",
              },
              "& .MuiPickersDay-root.Mui-selected": {
                backgroundColor: "#10b981",
                color: "white",
                "&:hover": {
                  backgroundColor: "#059669",
                },
                "&:focus": {
                  backgroundColor: "#059669",
                },
              },
              "& .MuiDayCalendar-header": {
                paddingTop: "0px",
                color: theme === "dark" ? "white" : "black",
              },
              "& .MuiPickersCalendarHeader-root": {
                paddingLeft: "8px",
                paddingRight: "8px",
                color: theme === "dark" ? "white" : "black",
              },
            }}
          />
        </LocalizationProvider>
      </div>
    );
  };
  const KAJABI_CHECKOUT_URL =
    "https://leslie-chen.mykajabi.com/offers/dX7bB3UD";

  const handleBuyNow = () => {
    window.location.href = KAJABI_CHECKOUT_URL;
  };

  // Get sorted assignment days
  const assignmentDays = Object.keys(assignments)
    .map((day) => parseInt(day))
    .sort((a, b) => b - a); // Sort descending (most recent first)

  const sidebarBgClass =
    theme === "dark"
      ? "bg-gradient-to-b from-[#0f2027] via-[#203a43] to-[#2c5364]"
      : "bg-gradient-to-b from-[#10b981] to-[#059669]";

  const dayBadgeClass =
    theme === "dark"
      ? "bg-emerald-600/30 border border-emerald-500/50 text-emerald-100"
      : "bg-white/20 border border-white/30 text-white";

  const closeButtonClass =
    theme === "dark"
      ? "text-emerald-200 hover:bg-emerald-600/20"
      : "text-white hover:bg-white/20";

  const userInfoBgClass =
    theme === "dark"
      ? "bg-slate-800/50 border-slate-600"
      : "bg-emerald-800/30 border-emerald-400/30";

  return (
    <>
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/80 z-30 md:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      <div
        className={`sidebar fixed inset-y-0 left-0 z-30 w-66 transform ${sidebarBgClass} transition-all duration-300 ease-in-out md:relative md:translate-x-0 ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="flex h-full flex-col">
          <div className="p-4 flex items-center justify-between md:justify-end h-14 sm:h-auto md:h-16">
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-2xl shadow text-xs sm:text-sm font-semibold md:hidden ${dayBadgeClass}`}
            >
              <div
                className={`p-1 rounded-full ${theme === "dark" ? "bg-emerald-500/30" : "bg-white/30"
                  }`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 sm:h-5 sm:w-5 ${theme === "dark" ? "text-emerald-200" : "text-white"
                    }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10m-11 4h12m-6 4v1m-6 0h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <span>
                Day{" "}
                <span className="font-bold">
                  {localStorage.getItem("courseDay")}
                </span>{" "}
                of 42
              </span>
            </div>
            <button
              className={`rounded-full p-1 md:hidden ${closeButtonClass}`}
              onClick={toggleSidebar}
            >
              <CloseIcon size={20} />
            </button>
          </div>

          <nav className="mt-4 px-1 flex-1 space-y-2 overflow-y-auto">
            <SidebarItem
              label="My Victories"
              isOpen={openItemLabel === "victories"}
              onClick={() => handleShowVictories()}
            ></SidebarItem>

            <SidebarItem
              label="My Reflections"
              expandable
              isOpen={openItemLabel === "assignments"}
              onClick={() => handleItemClick("assignments")}
            >
              <div
                className={`space-y-2 transition-all duration-300 ${theme !== "dark" ? "bg-slate-100" : ""
                  }`}
              >
                <div className="p-2 max-h-48 overflow-y-auto">
                  {assignmentDays.length > 0 ? (
                    <div className="group/global">
                      <ul className="space-y-2 pl-4 list-disc list-inside marker:text-emerald-700">
                        {assignmentDays.map((dayNumber) => (
                          <li
                            key={dayNumber}
                            className={`py-1 px-2 rounded-lg text-sm cursor-pointer transition-all duration-300 marker:text-emerald-700 hover:scale-[104%] ${theme === "dark"
                              ? "text-slate-200 hover:bg-slate-700/50 hover:text-emerald-300"
                              : "text-emerald-900 hover:pl-2 hover:text-emerald-950"
                              }`}
                            onClick={() =>
                              handleAssignmentClick(
                                dayNumber,
                                assignments[dayNumber]
                              )
                            }
                          >
                            <span className="transition-all duration-300">
                              Day {dayNumber}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <p
                      className={`pl-4 text-xs transition-all duration-200 ${theme === "dark" ? "text-slate-400" : "text-black"
                        }`}
                    >
                      Submitted assignments will be shown here.
                    </p>
                  )}
                </div>
              </div>
            </SidebarItem>

            <SidebarItem
              label="The Guided Path "
              expandable
              isOpen={openItemLabel === "journey"}
              onClick={() => handleItemClick("journey")}
            >
              <div
                className={`space-y-2 transition-all duration-300 ${theme !== "dark" ? "bg-slate-100" : ""
                  }`}
              >
                <div className="p-2 max-h-48 overflow-y-auto">
                  {courseDay > 0 ? (
                    <div className="group/global">
                      <ul className="space-y-2 pl-4 list-disc list-inside marker:text-emerald-700">
                        {Array.from(
                          { length: Number(courseDay) + 1 },
                          (_, index) => (
                            <li
                              key={index}
                              className={`py-1 px-2 rounded-lg text-sm cursor-pointer transition-all duration-300 marker:text-emerald-700 hover:scale-[104%] ${theme === "dark"
                                ? "text-slate-200 hover:bg-slate-700/50 hover:text-emerald-300"
                                : "text-emerald-900  hover:pl-2 hover:text-emerald-950"
                                }`}
                              onClick={() => fetchChatHistory(index)}
                            >
                              <span className="transition-all duration-300">
                                {index === 0 ? "Onboarding" : `Day ${index}`}
                              </span>
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  ) : (
                    <p
                      className={`pl-4 text-xs transition-all duration-200 ${theme === "dark" ? "text-slate-400" : "text-black"
                        }`}
                    >
                      Your daily training history will be shown here.
                    </p>
                  )}
                </div>
              </div>
            </SidebarItem>

            <SidebarItem
              label="Resource Hub"
              expandable
              isOpen={openItemLabel === "library"}
              onClick={() => handleItemClick("library")}
            >
              <div className={`p-2 ${theme !== "dark" ? "bg-slate-100" : ""}`}>
                <div className="ms-2 pb-2 max-h-48 overflow-y-auto">
                  {(() => {
                    let anyMatch = false;

                    const content = (
                      <div className="group/global">
                        {dayItems.map((day, i) => {
                          const validItems = day.items.filter(
                            (item) =>
                              item.fileType !== "other" &&
                              item.name &&
                              item.name
                                .toLowerCase()
                                .includes(appliedQuery.toLowerCase())
                          );

                          if (validItems.length === 0) return null;
                          anyMatch = true;

                          return (
                            <div key={i}>
                              <div
                                className={`font-semibold mb-1 text-sm transition-all duration-200 ${theme === "dark"
                                  ? "text-emerald-700"
                                  : "text-emerald-900"
                                  }`}
                              >
                                {day.day === 0
                                  ? "Onboarding"
                                  : `Day ${day.day}`}
                                :
                              </div>
                              <ul
                                className={`ml-4 list-disc transition-all duration-300 ${theme === "dark"
                                  ? "text-slate-200"
                                  : "text-emerald-700"
                                  }`}
                              >
                                {validItems.map((item, j) => (
                                  <li
                                    key={j}
                                    className={`m-2 transition-all duration-300 marker:text-emerald-700 hover:scale-[104%] ${theme === "dark"
                                      ? "hover:text-emerald-400"
                                      : "hover:text-emerald-900"
                                      }`}
                                  >
                                    <button
                                      onClick={() => setSelectedItem(item)}
                                      className="block max-w-[250px] truncate text-left text-sm"
                                      title={item.name}
                                    >
                                      {item.name}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    );

                    return anyMatch ? (
                      content
                    ) : (
                      <p
                        className={`text-xs ${theme === "dark" ? "text-slate-400" : "text-black"
                          }`}
                      >
                        Training materials will be shown here.
                      </p>
                    );
                  })()}
                </div>

                <div
                  className={`flex items-center w-full border rounded-full ${theme === "dark"
                    ? "border-slate-600 bg-slate-700/50"
                    : "border-slate-700 bg-white/10"
                    }`}
                >
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      const value = e.target.value;
                      setSearchQuery(value);
                      if (value.trim() === "") {
                        setAppliedQuery("");
                      }
                    }}
                    placeholder="Type here..."
                    className={`w-full px-2 rounded-full h-6 ms-1 text-xs focus:outline-none ${theme === "dark"
                      ? "bg-slate-700 text-slate-200 placeholder-slate-400"
                      : "bg-slate-200 text-slate-900 placeholder-slate-800"
                      }`}
                  />
                  <button
                    className={`rounded-full m-1 text-[8px] py-2 px-3.5 font-semibold transition-colors ${theme === "dark"
                      ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                      : "bg-emerald-300 border-1 border-slate-500 text-emerald-600 hover:bg-emerald-400"
                      }`}
                    onClick={() => setAppliedQuery(searchQuery)}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19C12.8487 19 14.551 18.3729 15.9056 17.3199L19.2929 20.7071C19.6834 21.0976 20.3166 21.0976 20.7071 20.7071C21.0976 20.3166 21.0976 19.6834 20.7071 19.2929L17.3199 15.9056C18.3729 14.551 19 12.8487 19 11C19 6.58172 15.4183 3 11 3ZM5 11C5 7.68629 7.68629 5 11 5C14.3137 5 17 7.68629 17 11C17 14.3137 14.3137 17 11 17C7.68629 17 5 14.3137 5 11Z"
                        fill="currentColor"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </SidebarItem>

            <SidebarItem
              label="My Space"
              expandable
              isOpen={openItemLabel === "user"}
              onClick={() => handleItemClick("user")}
            >
              <div className="space-y-2 mx-2 mb-2">
                <div className="group/global">
                  {/* <div
                    className={`flex items-center rounded-lg px-3 py-2 transition-all duration-300 hover:scale-[104%] ${
                      theme === "dark"
                        ? "hover:bg-slate-700/50"
                        : "hover:bg-white/20"
                    }`}
                  >
                    <span
                      className={`flex w-full items-center justify-between text-sm transition-all duration-300 cursor-pointer ${
                        theme === "dark"
                          ? "text-slate-200 hover:text-emerald-300"
                          : "text-emerald-700 hover:text-emerald-900 hover:pl-2"
                      }`}
                      onClick={() => setShowSubscription(true)}
                    >
                      <span className="transition-all duration-300">
                        My Subscription Plan
                      </span>
                    </span>
                  </div> */}

                  <div
                    className={`flex items-center rounded-lg px-3 py-2 transition-all duration-300 hover:scale-[104%] ${theme === "dark"
                      ? "hover:bg-slate-700/50"
                      : "hover:bg-white/20"
                      }`}
                  >
                    <span
                      className={`flex w-full items-center justify-between text-sm transition-all duration-300 cursor-pointer ${theme === "dark"
                        ? "text-slate-200 hover:text-emerald-300"
                        : "text-emerald-700 hover:text-emerald-900 hover:pl-2"
                        }`}
                      onClick={() => (window.location.href = "/profile")}
                    >
                      <span className="transition-all duration-300">
                        Profile Settings
                      </span>
                    </span>
                  </div>
                  <div
                    className={`flex items-center rounded-lg px-3 py-2 transition-all duration-300 hover:scale-[104%] ${theme === "dark"
                      ? "hover:bg-slate-700/50"
                      : "hover:bg-white/20"
                      }`}
                  >
                    <span
                      onClick={() => setShowSupport(true)}
                      className={`flex w-full items-center text-sm transition-all duration-300 cursor-pointer ${theme === "dark"
                        ? "text-slate-200 hover:text-emerald-300"
                        : "text-emerald-700 hover:text-emerald-900 hover:pl-2"
                        }`}
                    >
                      <span className="transition-all duration-300">
                        Need Support?
                      </span>
                    </span>
                  </div>

                  <div
                    className={`flex items-center rounded-lg px-3 py-2 transition-all duration-300 hover:scale-[104%] ${theme === "dark"
                      ? "hover:bg-slate-700/50"
                      : "hover:bg-white/20"
                      }`}
                  >
                    <span
                      className={`flex w-full items-center text-sm transition-all duration-300 cursor-pointer ${theme === "dark"
                        ? "text-slate-200 hover:text-emerald-300"
                        : "text-emerald-700 hover:text-emerald-900 hover:pl-2"
                        }`}
                      onClick={async () => {
                        await logout();
                        toast.success("Logout successful!");
                        window.location.reload();
                      }}
                    >
                      <span className="transition-all duration-300">
                        Logout
                      </span>
                      <svg
                        width="25"
                        height="25"
                        viewBox="0 0 1024 980"
                        className="ml-2"
                        version="1.1"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <defs>
                          <mask id="logout-mask">
                            <rect width="100%" height="100%" fill="white" />
                            <circle
                              cx="512.3"
                              cy="511.8"
                              r="391.3"
                              fill="black"
                            />
                          </mask>
                        </defs>
                        <g>
                          <path
                            d="M512.3 959C265.3 959 65 758.8 65 511.8S265.3 64.5 512.3 64.5s447.2 200.2 447.2 447.2S759.3 959 512.3 959z"
                            fill="#ff0000"
                            mask="url(#logout-mask)"
                          />
                          <path
                            d="M664.9 330.1c12.8-13.3 44.2 13.3 44.2 13.3 34.3 44.2 54.8 100.3 54.8 161.2 0 142.9-112.6 258.7-251.6 258.7S260.7 647.5 260.7 504.6c0-61.9 21.1-118.7 56.3-163.2 0 0 27.5-24.4 41.5-11.4s10.4 35.9 2.3 46.1c-28.5 36-44.3 80.9-44.3 128.5 0 112.3 88 202.8 195.7 202.8S707.9 617 707.9 504.6c0-46.9-15.4-91.2-43.1-127-7.9-10.2-12.8-34.2 0.1-47.5z"
                            fill="#ff0000"
                          />
                          <path
                            d="M484.3 288.1c0-15.4 12.4-27.9 28-27.9 15.4 0 28 12.5 28 27.9v195.8c0 15.4-12.4 27.9-28 27.9-15.4 0-28-12.5-28-27.9V288.1z"
                            fill="#ff0000"
                          />
                        </g>
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            </SidebarItem>

            {diffDays >= 42 && !isSelectedPlan && (
              <SidebarItem
                label="Join Ascendra Plus"
                onClick={() => handleInstinctFormulaPay()}
              ></SidebarItem>
            )}
          </nav>

          <div className="px-1 pb-20 space-y-3">
            <div
              className={`px-2 py-1 text-[10px] text-center opacity-80 ${theme === "dark" ? "text-gray-400" : "text-gray-600"
                }`}
            >
              Beta Version. Report issues{" "}
              <a
                href="https://forms.gle/d1BTw7kq1VduBLLXA"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-emerald-500"
              >
                here
              </a>{" "}
              or contact@ascendra.me
            </div>
            {(hasActiveSubscription || diffDays < 49 || isSelectedPlan) && (
              <SidebarItem
                label="Ask Leslie"
                panic
                onClick={() => setShowInstantChat(true)}
              />
            )}

            {Number(localStorage.getItem("courseDay")) >= 42 && (
              <SidebarItem
                label="Subscribe to Ascendra Plus"
                panic
                onClick={() => handleBuyNow()}
              />
            )}
          </div>

          <div
            className={`border-t p-4 flex items-center gap-3 ${userInfoBgClass}`}
          >
            <div
              onClick={() => setShowProfileModal(true)}
              className={`${uploadingImage ? "cursor-wait opacity-70" : "cursor-pointer"
                } ${theme === "dark" ? "text-emerald-200" : "text-white"}`}
              style={{ position: "relative", display: "inline-block" }}
            >
              {profileImage ? (
                <img
                  src={profileImage}
                  alt="Profile"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    objectFit: "cover",
                    // border: uploadingImage ? '2px solid #9ca3af' : '2px solid #069c27'
                  }}
                />
              ) : (
                <Person style={{ fontSize: 40 }} />
              )}
              {uploadingImage && (
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor: "rgba(0, 0, 0, 0.5)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <div
                    style={{
                      width: 16,
                      height: 16,
                      border: "2px solid #fff",
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                </div>
              )}
            </div>

            <div className="text-sm">
              <div
                className={`font-semibold truncate ${theme === "dark" ? "text-emerald-100" : "text-white"
                  }`}
              >
                {localStorage.getItem("userName") || "Username"}
              </div>
              <div
                className={`truncate text-xs ${theme === "dark" ? "text-slate-300" : "text-white/80"
                  }`}
              >
                {user?.email || "user@example.com"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <DialogModal
          setSelectedDay={setSelectedDay}
          selectedDay={selectedDay}
          setShowModal={setShowModal}
          showModal={showModal}
          selectedDayProgress={selectedDayProgress}
          themeContext={theme}
        />
      )}

      {showSubscription && (
        <SubscriptionModal
          showSubscription={showSubscription}
          setShowSubscription={setShowSubscription}
        />
      )}

      {showInstantChat && (
        <InstantChatModal
          setShowInstantChat={setShowInstantChat}
          showInstantChat={showInstantChat}
        />
      )}

      {showChatHistoryModal && (
        <ChatHistoryModal
          chatHistory={history}
          setShowChatHistoryModal={setShowChatHistoryModal}
          selectedDay={selectedDay}
          themeContext={theme}
        />
      )}

      {/* {showPayment && (
        <PaymentModal
          showPayment={showPayment}
          setShowPayment={setShowPayment}
          diffDays={diffDays}
        />
      )} */}
      {showPayment && (
        <AscendraPlusModal
          showPayment={showPayment}
          setShowPayment={setShowPayment}
        />
      )}

      {selectedItem && (
        <PreviewModal
          item={selectedItem}
          onClose={() => setSelectedItem(null)}
        />
      )}

      {showAssignments && selectedAssignment && (
        <AssignmentsModal
          showAssignments={showAssignments}
          setShowAssignments={setShowAssignments}
          assignment={selectedAssignment}
          dayNumber={selectedAssignmentDay}
          themeContext={theme}
        />
      )}

      <ProfileImageModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        profileImage={profileImage}
        onImageUpload={handleImageUpload}
        uploadingImage={uploadingImage}
      />

      <SupportModal showSupport={showSupport} setShowSupport={setShowSupport} />
    </>
  );
};

export default Sidebar;
